import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Tooltip, Collapse, ToggleButtonGroup, ToggleButton,
  useTheme,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  VpnKey as TokenIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';
import apiClient from '../api/client';
import type { AuditLogEntry } from '../types/models';

interface DiagnosticToken {
  id: string;
  label: string;
  name?: string;
  tokenPreview: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  isActive: boolean;
  isExpired?: boolean;
}

interface DiagnosticError {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  source: string;
  exceptionType?: string;
  stackTrace?: string;
  requestMethod?: string;
  requestPath?: string;
  httpStatusCode?: number;
  userId?: string;
  requestBody?: string;
  queryString?: string;
  userAgent?: string;
  clientIp?: string;
  durationMs?: number;
  correlationId?: string;
  additionalContext?: string;
}

interface HealthSummary {
  period: string;
  totalErrors: number;
  byLevel: Record<string, number>;
  averageDurationMs: number;
}

interface PlatformUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
}


// ---- Diagnostic Tokens Section ----
function DiagnosticTokensSection() {
  const [tokens, setTokens] = useState<DiagnosticToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [newTokenValue, setNewTokenValue] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/diagnostics/tokens').then(r => r.data);
      setTokens(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleCreate = async () => {
    if (!tokenName.trim()) return;
    setCreateLoading(true);
    try {
      const result = await apiClient.post('/diagnostics/tokens', { label: tokenName, expiryHours: 24 }).then(r => r.data);
      setNewTokenValue(result.token);
      // Store token for use by the errors section
      sessionStorage.setItem('diagnosticToken', result.token);
      setTokenName('');
      loadTokens();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create token');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await apiClient.delete(`/diagnostics/tokens/${id}`);
      loadTokens();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke token');
    }
  };

  const handleCopy = () => {
    if (newTokenValue) {
      navigator.clipboard.writeText(newTokenValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <LinearProgress color="error" />;

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TokenIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Diagnostic Tokens
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowCreate(true)}
          >
            Create Token
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {newTokenValue && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Tooltip title={copied ? 'Copied' : 'Copy token'}>
                <IconButton size="small" onClick={handleCopy}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
            onClose={() => setNewTokenValue(null)}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Token created. Copy it now — it will not be shown again.</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
              {newTokenValue}
            </Typography>
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Token Preview</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Last Used</TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map(token => (
                <TableRow key={token.id} hover>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>{token.label || token.name || '(unnamed)'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {token.tokenPreview}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={token.isActive ? 'Active' : 'Revoked'}
                      size="small"
                      color={token.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {new Date(token.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {token.isActive && (
                      <Tooltip title="Revoke token">
                        <IconButton size="small" color="error" onClick={() => handleRevoke(token.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tokens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No diagnostic tokens</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Create Diagnostic Token</DialogTitle>
          <DialogContent>
            <TextField
              label="Token Name"
              fullWidth
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
              placeholder="e.g. CI Pipeline, Monitoring"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleCreate} disabled={createLoading || !tokenName.trim()}>
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---- Recent Errors Section ----
function RecentErrorsSection() {
  const theme = useTheme();
  const [errors, setErrors] = useState<DiagnosticError[]>([]);
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [manualToken, setManualToken] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [errPage, setErrPage] = useState(0);
  const [errRowsPerPage, setErrRowsPerPage] = useState(25);

  const getToken = () => sessionStorage.getItem('diagnosticToken') || '';

  const loadErrors = useCallback(async () => {
    const token = sessionStorage.getItem('diagnosticToken');
    if (!token) {
      setFetchError('No diagnostic token active. Create one above to view errors.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.allSettled([
        apiClient.get('/diagnostics/entries', {
          params: { limit: 50 },
          headers: { 'X-Diagnostic-Token': token },
        }).then(r => r.data),
        apiClient.get('/diagnostics/summary', {
          params: { hours: 1 },
          headers: { 'X-Diagnostic-Token': token },
        }).then(r => r.data),
      ]);
      if (entriesRes.status === 'fulfilled') {
        setErrors(entriesRes.value);
        setFetchError(null);
      } else {
        const err = entriesRes.reason;
        if (err?.response?.status === 401) {
          setFetchError('Diagnostic token expired or invalid. Create a new one above.');
          sessionStorage.removeItem('diagnosticToken');
        } else {
          setFetchError(err?.response?.data?.message || err?.message || 'Failed to load errors');
        }
      }
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount (immediately uses sessionStorage token if present)
  useEffect(() => { loadErrors(); }, [loadErrors]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (autoRefresh && getToken()) {
      intervalRef.current = setInterval(loadErrors, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, loadErrors]);

  const levelColors: Record<string, string> = {
    Error: theme.palette.error.main,
    Warning: theme.palette.warning.main,
    Critical: '#FF1744',
  };

  const filteredErrors = levelFilter === 'All'
    ? errors
    : errors.filter(e => e.level === levelFilter);

  const paginatedErrors = filteredErrors.slice(errPage * errRowsPerPage, errPage * errRowsPerPage + errRowsPerPage);

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        {/* Health Summary Card */}
        {summary && !fetchError && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <SpeedIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Health Summary (Last Hour)
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ color: summary.totalErrors > 0 ? 'error.main' : 'success.main' }}>
                {summary.totalErrors} error{summary.totalErrors !== 1 ? 's' : ''}
              </Typography>
              {summary.byLevel?.Warning != null && (
                <Typography variant="body2" sx={{ color: summary.byLevel.Warning > 0 ? 'warning.main' : 'text.secondary' }}>
                  {summary.byLevel.Warning} warning{summary.byLevel.Warning !== 1 ? 's' : ''}
                </Typography>
              )}
              {summary.byLevel?.Critical != null && (
                <Typography variant="body2" sx={{ color: summary.byLevel.Critical > 0 ? '#FF1744' : 'text.secondary' }}>
                  {summary.byLevel.Critical} critical
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Avg response: {Math.round(summary.averageDurationMs)}ms
              </Typography>
            </Stack>
          </Box>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
            Recent Errors
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={autoRefresh ? 'Auto-refresh on (30s)' : 'Auto-refresh off'}>
              <Chip
                label={autoRefresh ? 'Live' : 'Paused'}
                size="small"
                color={autoRefresh ? 'success' : 'default'}
                variant="outlined"
                onClick={() => setAutoRefresh(!autoRefresh)}
                sx={{ cursor: 'pointer', fontWeight: 600 }}
              />
            </Tooltip>
            <IconButton size="small" onClick={loadErrors} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Token input for viewing errors */}
        {!getToken() && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Paste diagnostic token to view errors"
              value={manualToken}
              onChange={e => setManualToken(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                if (manualToken.trim()) {
                  sessionStorage.setItem('diagnosticToken', manualToken.trim());
                  setManualToken('');
                  loadErrors();
                }
              }}
            >
              Use Token
            </Button>
          </Stack>
        )}

        {/* Severity filter buttons */}
        {!fetchError && (
          <ToggleButtonGroup
            value={levelFilter}
            exclusive
            onChange={(_, val) => { if (val !== null) setLevelFilter(val); }}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="All" sx={{ textTransform: 'none', px: 2 }}>All ({errors.length})</ToggleButton>
            <ToggleButton value="Critical" sx={{ textTransform: 'none', px: 2, color: '#FF1744' }}>Critical ({errors.filter(e => e.level === 'Critical').length})</ToggleButton>
            <ToggleButton value="Error" sx={{ textTransform: 'none', px: 2, color: 'error.main' }}>Error ({errors.filter(e => e.level === 'Error').length})</ToggleButton>
            <ToggleButton value="Warning" sx={{ textTransform: 'none', px: 2, color: 'warning.main' }}>Warning ({errors.filter(e => e.level === 'Warning').length})</ToggleButton>
          </ToggleButtonGroup>
        )}

        {loading && <LinearProgress color="error" sx={{ mb: 1 }} />}
        {fetchError && <Alert severity="info" sx={{ mb: 2 }}>{fetchError}</Alert>}

        {!fetchError && (<>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, width: 40 }}></TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Timestamp</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Level</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedErrors.map(err => (
                  <React.Fragment key={err.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedId(expandedId === err.id ? null : err.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ width: 40, py: 0.5 }}>
                        {expandedId === err.id
                          ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                          {new Date(err.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={err.level}
                          size="small"
                          sx={{
                            bgcolor: `${levelColors[err.level] || 'text.secondary'}20`,
                            color: levelColors[err.level] || 'text.secondary',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                          {err.source}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.primary', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {err.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {/* Expandable detail row */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedId === err.id ? undefined : 'none' }}>
                        <Collapse in={expandedId === err.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 1 }}>
                            <Stack spacing={1}>
                              {err.exceptionType && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  <strong>Exception:</strong> {err.exceptionType}
                                </Typography>
                              )}
                              {err.requestMethod && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  <strong>Request:</strong> {err.requestMethod} {err.requestPath}
                                  {err.httpStatusCode ? ` (${err.httpStatusCode})` : ''}
                                  {err.durationMs != null ? ` - ${err.durationMs}ms` : ''}
                                </Typography>
                              )}
                              {err.userId && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  <strong>User:</strong> {err.userId}
                                </Typography>
                              )}
                              {err.userAgent && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  <strong>User Agent:</strong> {err.userAgent}
                                </Typography>
                              )}
                              {err.clientIp && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  <strong>Client IP:</strong> {err.clientIp}
                                </Typography>
                              )}
                              {err.correlationId && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  <strong>Correlation ID:</strong> {err.correlationId}
                                </Typography>
                              )}
                              {err.requestBody && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Request Body:</Typography>
                                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, p: 1, mt: 0.5, maxHeight: 150, overflow: 'auto' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                      {err.requestBody}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              {err.stackTrace && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Stack Trace:</Typography>
                                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, p: 1, mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                      {err.stackTrace}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              {err.additionalContext && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Additional Context:</Typography>
                                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, p: 1, mt: 0.5, maxHeight: 100, overflow: 'auto' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                      {err.additionalContext}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {!loading && filteredErrors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {errors.length === 0 ? 'No recent errors' : `No ${levelFilter.toLowerCase()} entries`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredErrors.length}
            rowsPerPage={errRowsPerPage}
            page={errPage}
            onPageChange={(_, p) => setErrPage(p)}
            onRowsPerPageChange={(e) => { setErrRowsPerPage(parseInt(e.target.value)); setErrPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>)}
      </CardContent>
    </Card>
  );
}

// ---- Platform Users Section ----
function PlatformUsersSection() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', displayName: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformApi.getUsers();
      // Filter to platform admin users
      setUsers(data.filter((u: PlatformUser) => u.role === 'PlatformAdmin'));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pw = '';
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const handleCreate = async () => {
    if (!newAdmin.email || !newAdmin.displayName || !newAdmin.password) {
      setAddError('All fields are required');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await platformApi.createPlatformAdmin(newAdmin);
      setShowAdd(false);
      setNewAdmin({ email: '', password: '', displayName: '' });
      loadUsers();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setAddError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to create admin');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) return <LinearProgress color="error" />;

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
            Platform Admin Users
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={() => setShowAdd(true)}
          >
            Add Admin
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>MFA</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Last Login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>{u.displayName}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={u.isActive ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.mfaEnabled ? 'Enabled' : 'Off'}
                      size="small"
                      color={u.mfaEnabled ? 'success' : 'default'}
                      variant={u.mfaEnabled ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No platform admin users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Platform Admin</DialogTitle>
          <DialogContent>
            {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Display Name"
                fullWidth
                value={newAdmin.displayName}
                onChange={e => setNewAdmin(prev => ({ ...prev, displayName: e.target.value }))}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={newAdmin.email}
                onChange={e => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Password"
                  fullWidth
                  value={newAdmin.password}
                  onChange={e => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  helperText="Min 8 chars, upper, lower, digit, special"
                />
                <Button
                  variant="text"
                  size="small"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => setNewAdmin(prev => ({ ...prev, password: generatePassword() }))}
                >
                  Generate
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleCreate} disabled={addLoading}>
              {addLoading ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---- Platform Audit Logs Section ----
function PlatformAuditLogsSection() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTenant, setFilterTenant] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(25);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: 100 };
      if (filterTenant.trim()) params.tenantId = filterTenant.trim();
      if (filterAction.trim()) params.action = filterAction.trim();
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const data = await platformApi.getAuditLogs(params);
      setLogs(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filterTenant, filterAction, filterFrom, filterTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const truncateId = (id: string | null) => {
    if (!id) return '-';
    return id.length > 12 ? id.slice(0, 12) + '...' : id;
  };

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
            Platform Audit Logs
          </Typography>
          <IconButton size="small" onClick={loadLogs} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Filters */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Tenant ID"
            value={filterTenant}
            onChange={e => setFilterTenant(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="Action"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            placeholder="e.g. Created, Updated"
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="From"
            type="date"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <Button variant="outlined" size="small" onClick={loadLogs} disabled={loading}>
            Apply
          </Button>
        </Stack>

        {loading && <LinearProgress color="error" sx={{ mb: 1 }} />}

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Timestamp</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Tenant ID</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>User ID</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Entity Type</TableCell>
                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.slice(auditPage * auditRowsPerPage, auditPage * auditRowsPerPage + auditRowsPerPage).map(log => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.tenantId || ''}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {truncateId(log.tenantId)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.userId || ''}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {truncateId(log.userId)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.action} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                      {log.entityType || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.detail || ''}>
                      <Typography variant="body2" sx={{ color: 'text.primary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.detail || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No audit logs found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={logs.length}
          rowsPerPage={auditRowsPerPage}
          page={auditPage}
          onPageChange={(_, p) => setAuditPage(p)}
          onRowsPerPageChange={(e) => { setAuditRowsPerPage(parseInt(e.target.value)); setAuditPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </CardContent>
    </Card>
  );
}

// ---- Main Security Page ----
export default function PlatformSecurityPage() {
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SecurityIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Security & Diagnostics</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Token management, error monitoring, and platform admin users
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2.5}>
        <DiagnosticTokensSection />
        <RecentErrorsSection />
        <PlatformUsersSection />
        <PlatformAuditLogsSection />
      </Stack>
    </Box>
  );
}
